import React from 'react';
import { useRef } from 'react';
import GenericPropertiesControl from './GenericPropertiesControl';
import { useSitePropertiesStore } from '../../../store/useGeometryPropertiesStore';

const SitePropertiesControl = () => {
  const store = useSitePropertiesStore((state) => state);
  
  // Parse JSON value from store
  const parseJsonValue = (value) => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  // Generate dropdown options from object values (for gfz, bmz, grz)
  const generateOptionsFromObject = (objectValue) => {
    const parsed = parseJsonValue(objectValue);
    if (!parsed || typeof parsed !== 'object') {
      return [];
    }
  
      return Object.entries(parsed).map(([key, value]) => ({
        
      label: `${key}: ${value}`,
      value: { [key]: value }  // ✅ Each option represents just its own key-value pair
    }));
  };

  const generateOptionsFromArray = (arrayValue) => {
    if (!arrayValue) return [];
    
    // If it's an array, get the first element
    if (Array.isArray(arrayValue)) {
      if (arrayValue.length === 0) return [];
      let cleanedString = String(arrayValue[0]);
      cleanedString = cleanedString.replace(/^\[/, '').replace(/\]$/, '');
      const items = cleanedString.match(/'[^']+'/g) || [];
      
      // Clean up each item
      const cleanedItems = items.map(item => 
        item.replace(/'/g, '').trim()
      ).filter(item => item !== '');
      
      return cleanedItems.map(item => ({
        label: item,
        value: item
      }));
    }
    let cleanedString = String(arrayValue);
    cleanedString = cleanedString.replace(/^\d+:\s*\[/, '');
    cleanedString = cleanedString.replace(/\]$/, '');
    const items = cleanedString.match(/'[^']+'/g) || [];
    
    // Clean up each item
    const cleanedItems = items.map(item => 
      item.replace(/'/g, '').trim()
    ).filter(item => item !== '');
    
    return cleanedItems.map(item => ({
      label: item,
      value: item
    }));
  };

  const originalGfz = useRef(parseJsonValue(store.gfz));
  const originalBmz = useRef(parseJsonValue(store.bmz));
  const originalGrz = useRef(parseJsonValue(store.grz));

  const gfzOptions = generateOptionsFromObject(originalGfz.current);
  const bmzOptions = generateOptionsFromObject(originalBmz.current);
  const grzOptions = generateOptionsFromObject(originalGrz.current);
  const originalNutzungsart = useRef(store.nutzungsart);
  const nutzungsartOptions = generateOptionsFromArray(originalNutzungsart.current)

  const properties = [
    { key: 'amtlicheFlaeche', label: 'Amtlichefläche', value: store.amtlicheFlaeche, tooltip:"Wird für rechtliche und administrative Zwecke verwendet. Die Fläche wird im amtlichen ebenen Koordinatensystem berechnet und nicht regelmäßig aktualisiert. Sie kann deutlich von der tatsächlichen Fläche abweichen."},
    { key: 'grundstuecksflaeche', label: 'Grundstuecksfläche', value: store.grundstuecksflaeche, tooltip: "Wird für GIS- und räumliche Analysen verwendet. Die Fläche wird auf dem Ellipsoid berechnet und entspricht der realen Grundstücksfläche deutlich genauer." },
    { key: 'flurnummer', label: 'Flurnummer', value: store.flurnummer},
    { key: 'flurstücknummer', label: 'Flurstücknummer', value: store.flurstücknummer },
    { key: 'gemarkungName', label: 'Gemarkung', value: store.gemarkungName },
    { key: 'gemarkungSchluessel', label: 'Gemarkungschlüssel', value: store.gemarkungSchluessel },
    { key: 'gemeindeSchluessel', label: 'Gemeindeschlüssel', value: store.gemeindeSchluessel},
    // { key: 'baugebiet', label: 'Baugebiet', value: store.baugebiet },
    { 
      key: 'nutzungsart', 
      label: 'Nutzungsart', 
      value: Array.isArray(store.nutzungsart) && store.nutzungsart.length > 0 ? store.nutzungsart[0] : store.nutzungsart,
      options: nutzungsartOptions.map(opt => opt.value)
    },
    { 
      key: 'gfz', 
      label: 'GFZ', 
      value: parseJsonValue(store.gfz),
      objectOptions: gfzOptions,
      tooltip:"Wird zur Bestimmung der maximal zulässigen Geschossfläche im Verhältnis zur Grundstücksfläche verwendet. Der angezeigte Wert ist ein Orientierungswert gemäß §17 BauNVO auf Basis der Nutzungsart und kann von den tatsächlichen Festsetzungen für dieses Grundstück abweichen. Verbindlich ist ausschließlich der jeweilige Bebauungsplan."
    },
    { 
      key: 'bmz', 
      label: 'BMZ', 
      value: parseJsonValue(store.bmz),
      objectOptions: bmzOptions,
      tooltip:"Wird zur Bestimmung des maximal zulässigen Baukörpervolumens im Verhältnis zur Grundstücksfläche verwendet, insbesondere bei gewerblich oder industriell genutzten Flächen. Der angezeigte Wert ist ein allgemeiner Orientierungswert und kann erheblich von den örtlich festgesetzten Werten abweichen. Der verbindliche Wert ist beim zuständigen Bauamt bzw. im Bebauungsplan zu erfragen."
    },
    { 
      key: 'grz', 
      label: 'GRZ', 
      value: parseJsonValue(store.grz),
      objectOptions: grzOptions,
      tooltip: "Wird zur Bestimmung der maximal zulässigen überbaubaren Grundstücksfläche verwendet. Der angezeigte Wert ist ein Orientierungswert gemäß §17 BauNVO auf Basis der Nutzungsart und entspricht nicht zwangsläufig den grundstücksspezifischen Festsetzungen des Bebauungsplans. Bitte mit dem geltenden Bebauungsplan abgleichen."
    },
    { key: 'erdbebenzone', label: 'Erdbebenzone', value: store.erdbebenzone },
    { key: 'untergrundklasse', label: 'Untergrundklasse', value: store.untergrundklasse },
    { key: 'windlastzone', label: 'Windlastzone', value: store.windlastzone },
    { key: 'schneelastzone', label: 'Schneelastzone', value: store.schneelastzone },
    { key: 'abstandflaechen', label: 'Abstandflaechen', value: store.abstandflaechen },
    // { key: 'Gebaeude', label: ' Gebäude auf der Flurstück', value: store.Gebaeude},
  ];

  const handlePropertyUpdate = (key, value) => {
    // Value is already a JSON string from the select for object types
    // For simple strings, just pass the value
    store.updateProperty(key, value);
  };

  return (
    <GenericPropertiesControl 
      properties={properties}
      onPropertyUpdate={handlePropertyUpdate}
      title="EIGENSCHAFTEN"
    />
  );
};

export default SitePropertiesControl;